import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Table, Card, CardHeader, CardBody } from 'reactstrap';
import { Line, Doughnut } from 'react-chartjs-2';
import moment from 'moment-timezone';
import ContentWrapper from '../../components/Layout/ContentWrapper';
import PageTitle from '../../components/Common/PageTitle';
import StatisticCard from '../../components/Cards/StatisticCard';
import { getMetrics } from '../../actions/metricsActions';

// Only "New Signups" has real historical granularity today (membership.inserted_at is
// append-only) — Revenue, Active Subscribers and Churn are current-state snapshots with
// no trend chart. Revenue is captioned since it's a catalog-price estimate, not the
// actual amount charged.
const GATEWAY_LABELS = { stripe: 'Stripe', apple: 'Apple', google: 'Google' };
const GATEWAY_COLORS = { stripe: '#5AC8FA', apple: '#8E8E93', google: '#34C759' };
const GATEWAY_LINKS = {
  stripe: 'https://dashboard.stripe.com',
  apple: 'https://appstoreconnect.apple.com',
  google: 'https://play.google.com/console',
};

const SEGMENT_LABELS = { personal: 'Personal (self-serve)', team: 'Team (B2B)' };

const METRIC_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: true, displayColors: false } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    y: { beginAtZero: false, grid: { color: '#eee' }, ticks: { font: { size: 10 } } },
  },
};

const MIX_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '65%',
  plugins: { legend: { display: false } },
};

const formatCurrency = (cents) => `$${Math.round((cents || 0) / 100).toLocaleString()}`;

const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

const churnPercent = (churn) => (churn.total > 0 ? (churn.pending_cancellation / churn.total) * 100 : 0);

const rowChurnPercent = (row) => {
  const denominator = row.active + row.pending_cancellation;
  return denominator > 0 ? (row.pending_cancellation / denominator) * 100 : 0;
};

const signupsDelta = (series) => {
  if (series.length < 2) return null;
  const current = series[series.length - 1].count;
  const previous = series[series.length - 2].count;
  if (previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs prior period`;
};

const metricChartData = (series, color) => ({
  labels: series.map((point) => moment(point.period_end).format('MMM D')),
  datasets: [
    {
      data: series.map((point) => point.count),
      borderColor: color,
      backgroundColor: color,
      borderWidth: 2,
      pointRadius: 2,
      tension: 0.3,
    },
  ],
});

const MetricChart = ({ series, color }) => (
  <div style={{ height: 120 }}>
    <Line data={metricChartData(series, color)} options={METRIC_CHART_OPTIONS} />
  </div>
);

const SegmentPanel = ({ segment }) => (
  <Card className="card-default card-container mb-3 h-100">
    <CardHeader>{SEGMENT_LABELS[segment.segment] || segment.segment}</CardHeader>
    <CardBody>
      <Row>
        <Col xs={6} className="mb-3">
          <div className="text-muted small">Revenue</div>
          <div className="h4 mb-0">{formatCurrency(segment.revenue)}</div>
        </Col>
        <Col xs={6} className="mb-3">
          <div className="text-muted small">Active</div>
          <div className="h4 mb-0">{segment.active.toLocaleString()}</div>
        </Col>
        <Col xs={6}>
          <div className="text-muted small">Signups</div>
          <div className="h4 mb-0">{segment.signups.toLocaleString()}</div>
        </Col>
        <Col xs={6}>
          <div className="text-muted small">Churn</div>
          <div className="h4 mb-0">{formatPercent(rowChurnPercent(segment))}</div>
        </Col>
      </Row>
    </CardBody>
  </Card>
);

class Metrics extends Component {
  componentDidMount() {
    this.props.doGetMetrics();
  }

  render() {
    const { metrics } = this.props;

    if (!metrics || !Array.isArray(metrics.breakdown_by_gateway)) {
      return (
        <ContentWrapper>
          <Row>
            <Col xs={12}>
              <PageTitle title="Metrics" icon="icon-chart" showTeam={false} />
            </Col>
          </Row>
        </ContentWrapper>
      );
    }

    const stats = {
      revenue: {
        label: 'Revenue',
        value: formatCurrency(metrics.revenue),
        caption: 'Estimated from catalog price',
        color: '#5AC8FA',
      },
      activeSubscribers: {
        label: 'Active Subscribers',
        value: metrics.active_subscribers.toLocaleString(),
        color: '#5AC8FA',
      },
      newSignups: {
        label: 'New Signups',
        value: (metrics.signups_time_series[metrics.signups_time_series.length - 1]?.count || 0).toLocaleString(),
        caption: signupsDelta(metrics.signups_time_series),
        series: metrics.signups_time_series,
        color: '#FF9500',
      },
      churn: {
        label: 'Churn',
        value: formatPercent(churnPercent(metrics.churn)),
        caption: 'Cancellations in progress, not period churn rate',
        color: '#FF3B30',
      },
    };

    const totalGatewayRevenue = metrics.breakdown_by_gateway.reduce((sum, row) => sum + row.revenue, 0);

    const mixChart = {
      labels: metrics.breakdown_by_gateway.map((row) => GATEWAY_LABELS[row.gateway] || row.gateway),
      datasets: [
        {
          data: metrics.breakdown_by_gateway.map((row) =>
            totalGatewayRevenue > 0 ? (row.revenue / totalGatewayRevenue) * 100 : 0
          ),
          backgroundColor: metrics.breakdown_by_gateway.map((row) => GATEWAY_COLORS[row.gateway] || '#CCCCCC'),
          borderWidth: 0,
        },
      ],
    };

    return (
      <ContentWrapper>
        <Row>
          <Col xs={12}>
            <PageTitle title="Metrics" icon="icon-chart" showTeam={false} />
          </Col>
        </Row>
        <div className="main-content-container">
          <Row>
            {Object.entries(stats).map(([key, stat]) => (
              <Col xs={12} md={3} key={key}>
                <StatisticCard
                  header={stat.label}
                  statistic={stat.value}
                  caption={stat.caption}
                  chart={stat.series && <MetricChart series={stat.series} color={stat.color} />}
                />
              </Col>
            ))}
          </Row>
          <Row className="mt-4">
            <Col xs={12} md={4}>
              <Card className="card-default card-container mb-3">
                <CardHeader>Revenue mix</CardHeader>
                <CardBody className="py-4">
                  <div className="d-flex align-items-center">
                    <div style={{ height: 150, width: 150 }}>
                      <Doughnut data={mixChart} options={MIX_OPTIONS} />
                    </div>
                    <div className="ml-4">
                      {metrics.breakdown_by_gateway.map((row, i) => (
                        <div key={row.gateway} className="d-flex align-items-center mb-1">
                          <div
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: GATEWAY_COLORS[row.gateway] || '#CCCCCC',
                            }}
                            className="mr-2"
                          />
                          <div>
                            {GATEWAY_LABELS[row.gateway] || row.gateway} — {formatPercent(mixChart.datasets[0].data[i])}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-muted small mt-2">Estimated from catalog price, not actual revenue collected.</div>
                </CardBody>
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card className="card-default card-container mb-3">
                <CardHeader>Breakdown by Gateway</CardHeader>
                <Table className="mb-0">
                  <thead>
                    <tr>
                      <th>Gateway</th>
                      <th>Revenue</th>
                      <th>Active</th>
                      <th>Signups</th>
                      <th>Churn</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.breakdown_by_gateway.map((row) => (
                      <tr key={row.gateway}>
                        <td>{GATEWAY_LABELS[row.gateway] || row.gateway}</td>
                        <td>{formatCurrency(row.revenue)}</td>
                        <td>{row.active.toLocaleString()}</td>
                        <td>{row.signups.toLocaleString()}</td>
                        <td>{formatPercent(rowChurnPercent(row))}</td>
                        <td>
                          {GATEWAY_LINKS[row.gateway] && (
                            <a
                              href={GATEWAY_LINKS[row.gateway]}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Open ${GATEWAY_LABELS[row.gateway] || row.gateway}`}
                              aria-label={`Open ${GATEWAY_LABELS[row.gateway] || row.gateway}`}
                            >
                              <em className="icon-arrow-right" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="text-muted small px-3 py-2">* Revenue is estimated from catalog price.</div>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            {metrics.breakdown_by_segment.map((segment) => (
              <Col xs={12} md={6} key={segment.segment}>
                <SegmentPanel segment={segment} />
              </Col>
            ))}
          </Row>
        </div>
      </ContentWrapper>
    );
  }
}

const mapStateToProps = ({ metrics }) => ({
  metrics: metrics.data,
});

const mapDispatchToProps = (dispatch) => ({
  doGetMetrics: () => dispatch(getMetrics()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Metrics);
